import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign } from 'lucide-react';

const PaymentCalculator = () => {
  const [bailAmount, setBailAmount] = useState('10000');
  const [downPayment, setDownPayment] = useState('100');
  const [paymentTerm, setPaymentTerm] = useState('6');
  const [results, setResults] = useState(null);

  useEffect(() => {
    calculatePayment();
  }, [bailAmount, downPayment, paymentTerm]);

  const calculatePayment = () => {
    const bail = parseFloat(bailAmount) || 0;
    const down = parseFloat(downPayment) || 0;
    const months = parseInt(paymentTerm) || 6;
    
    // Bond fee is typically 10% of bail amount in California
    const bondFee = bail * 0.10;
    const remainingBalance = bondFee - down;
    const monthlyPayment = remainingBalance / months;
    
    setResults({
      bondFee: bondFee,
      remainingBalance: remainingBalance,
      monthlyPayment: monthlyPayment > 0 ? monthlyPayment : 0,
      totalCost: bondFee
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
      <div className="text-center mb-8">
        <Calculator className="w-12 h-12 text-blue-600 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Payment Calculator</h3>
        <p className="text-gray-600">Adjust the values below to see your payment options</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <div className="space-y-6">
          <div>
            <label htmlFor="bailAmount" className="block text-sm font-bold text-gray-700 mb-2">
              Total Bail Amount
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="number"
                id="bailAmount"
                value={bailAmount}
                onChange={(e) => setBailAmount(e?.target?.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                placeholder="10000"
                min="1000"
                step="1000"
              />
            </div>
            <p className="text-sm text-gray-500 mt-1">Enter the full bail amount set by the court</p>
          </div>

          <div>
            <label htmlFor="downPayment" className="block text-sm font-bold text-gray-700 mb-2">
              Down Payment
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="number"
                id="downPayment"
                value={downPayment}
                onChange={(e) => setDownPayment(e?.target?.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                placeholder="100"
                min="0"
                step="50"
              />
            </div>
            <p className="text-sm text-gray-500 mt-1">Minimum $50 (or $0 with approved credit)</p>
          </div>

          <div>
            <label htmlFor="paymentTerm" className="block text-sm font-bold text-gray-700 mb-2">
              Payment Term (Months)
            </label>
            <select
              id="paymentTerm"
              value={paymentTerm}
              onChange={(e) => setPaymentTerm(e?.target?.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
            >
              <option value="3">3 months</option>
              <option value="6">6 months</option>
              <option value="9">9 months</option>
              <option value="12">12 months</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">Choose your preferred payment timeline</p>
          </div>
        </div>

        {/* Results Section */}
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
          <h4 className="text-xl font-bold text-gray-900 mb-6">Your Payment Plan</h4>
          
          {results && (
            <div className="space-y-4">
              <div className="flex justify-between py-2 border-b border-blue-200">
                <span className="text-gray-600">Bail Amount:</span>
                <span className="font-bold">{formatCurrency(bailAmount)}</span>
              </div>
              
              <div className="flex justify-between py-2 border-b border-blue-200">
                <span className="text-gray-600">Bond Fee (10%):</span>
                <span className="font-bold">{formatCurrency(results?.bondFee)}</span>
              </div>
              
              <div className="flex justify-between py-2 border-b border-blue-200">
                <span className="text-gray-600">Down Payment:</span>
                <span className="font-bold text-green-600">{formatCurrency(downPayment)}</span>
              </div>
              
              <div className="flex justify-between py-2 border-b border-blue-200">
                <span className="text-gray-600">Remaining Balance:</span>
                <span className="font-bold">{formatCurrency(results?.remainingBalance)}</span>
              </div>
              
              <div className="flex justify-between py-3 bg-blue-100 rounded-lg px-4 border-2 border-blue-300">
                <span className="text-gray-700 font-semibold">Monthly Payment:</span>
                <span className="font-black text-2xl text-blue-600">{formatCurrency(results?.monthlyPayment)}</span>
              </div>
            </div>
          )}
          
          <div className="mt-6">
            <a 
              href="tel:9168330052"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-xl font-bold text-lg text-center block transition-colors"
            >
              Apply for This Plan
            </a>
            <p className="text-center text-sm text-gray-600 mt-2">
              Call (916) 833-0052 for instant approval
            </p>
          </div>
        </div>
      </div>
      <div className="mt-8 bg-green-50 border border-green-200 rounded-xl p-6">
        <h4 className="text-lg font-bold text-green-800 mb-3">💡 Money-Saving Tips</h4>
        <ul className="space-y-2 text-green-700">
          <li>• Higher down payment = lower monthly payments</li>
          <li>• 0% down available with approved credit (OAC)</li>
          <li>• Flexible terms available - we work with your budget</li>
          <li>• No prepayment penalties - pay off early to save</li>
        </ul>
      </div>
    </div>
  );
};

export default PaymentCalculator;